/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { map, tap } from 'rxjs';
import { AUTH_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import { Pagination } from '~core/models/core.models';
import { ReferenceData } from '~core/models/reference-data.models';
import { ReferenceDataService } from '~core/services/reference-data.service';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OnboardingBillingEquipment } from '~core/models/sales-point.models';

@Component({
  selector: 'billing-equipment-declaration-form',
  standalone: false,
  templateUrl: './billing-equipment-declaration-form.component.html',
  styleUrl: './billing-equipment-declaration-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BillingEquipmentDeclarationFormComponent extends BaseAuthComponent {
  private _referenceService = inject(ReferenceDataService);

  billingEquipmentDeclarationform = new FormGroup({
    equipments: new FormArray([]),
    cashRegistersNumber: new FormControl({ value: null, disabled: false })
  });

  statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.onboarding.billingEquipmentFormValues`;
  equipmentDeclaration = signal<Record<string, boolean>>({
    usesBillingEquipment: false,
    usesCashRegisters: false
  });

  showOtherDesignation: boolean[] = [];
  isSubmitting = signal(false);
  selectedEquipmentType = signal<string | null>(null);

  billingEquipements$ = computed(() => {
    return this._referenceService
      .list({
        type: 'onboarding_equipment_type',
        sort: 'position',
        asc: 'position'
      })
      .pipe(map(data => data.items));
  });

  designations$ = computed(() => {
    return this._referenceService
      .list({
        type: 'billing_software',
        sort: 'position',
        asc: 'position',
        itemPerPage: 100_000_000
      })
      .pipe(map(data => data.items));
  });

  equipmentOptions = Object.values(OnboardingBillingEquipment);
  onboardingEquipment = OnboardingBillingEquipment;

  constructor() {
    super();
    this._addEquipementGroup();
  }

  onSubmit(): void {
    const payload = this.billingEquipmentDeclarationform.value;

    if (this.billingEquipmentDeclarationform.invalid) {
      this.billingEquipmentDeclarationform.markAllAsTouched();
      return;
    }

    this._eventService.emit({ type: 'GO_TO_NEXT_STEP' });
  }

  handleSlideChangeValue(event: MatSlideToggleChange): void {
    this.equipmentDeclaration.update(value => ({
      ...value,
      [event.source.id]: event.checked
    }));

    if (event.source.id === 'usesBillingEquipment') {
      this._updateEquipmentValidatorsBasedOnToggle();
    }

    if (event.source.id === 'usesCashRegisters') {
      this._updateCashRegistersValidatorsBasedOnToggle();
    }
  }

  handlePrevStep(): void {
    this._eventService.emit({ type: 'GO_TO_PREVIOUS_STEP' });
  }

  createEquipmentGroup(): FormGroup {
    return new FormGroup({
      type: new FormControl({ value: null, disabled: false }),
      name: new FormControl({ value: null, disabled: false }),
      otherValue: new FormControl({ value: null, disabled: false })
    });
  }

  addEquipement(): void {
    this._addEquipementGroup();
  }

  removeEquipement(index: number): void {
    this.equipements.removeAt(index);
    this.showOtherDesignation.splice(index, 1);
  }

  isFieldInvalid(index: number, fieldName: string): boolean {
    const control = this.equipements.at(index).get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  private _updateEquipmentValidatorsBasedOnToggle(): void {
    const usesBilling = this.equipmentDeclaration().usesBillingEquipment;
    this.equipements.controls.forEach(control =>
      this._applyEquipmentValidatorsToGroup(control as FormGroup, usesBilling)
    );
  }

  private _updateCashRegistersValidatorsBasedOnToggle(): void {
    const usesCashRegisters = this.equipmentDeclaration().usesCashRegisters;
    const control = this.billingEquipmentDeclarationform.get(
      'cashRegistersNumber'
    );
    control?.setValidators(usesCashRegisters ? [Validators.required] : null);

    if (usesCashRegisters) {
      control?.enable();
    } else {
      control?.disable();
      control?.setValue(null);
    }

    control?.updateValueAndValidity();
  }

  private _handleSelectedType(group: FormGroup): void {
    group
      .get('type')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(value => {
          this.selectedEquipmentType.set(value);
          if (value !== OnboardingBillingEquipment.billingSoftware) {
            group.get('name')?.reset(null);
            group.get('otherValue')?.reset(null);
          }
          this._updateNameValidators(group, value);
          this._updateOtherValueValidators(group);
          this._cdRef.markForCheck();
        })
      )
      .subscribe();
  }

  private _updateNameValidators(
    group: FormGroup,
    typeValue: string | null | undefined
  ): void {
    const nameControl = group.get('name');
    if (!nameControl) return;

    if (typeValue === OnboardingBillingEquipment.billingSoftware) {
      nameControl.setValidators([Validators.required]);
    } else {
      nameControl.clearValidators();
    }

    nameControl.updateValueAndValidity();
  }

  private _appendOthersOption(
    data: Pagination<ReferenceData>
  ): Pagination<ReferenceData> {
    return {
      ...data,
      items: [
        ...data.items,
        {
          id: 'autres',
          label: 'Autres',
          value: 'other',
          type: 'others',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ]
    };
  }

  private _handleDisplayOtherDesignation(group: FormGroup): void {
    const nameControl = group.get('name');
    if (!nameControl) return;

    const updateVisibility = (value: unknown): void => {
      const index = this.equipements.controls.indexOf(group);
      if (index === -1) return;
      this.showOtherDesignation[index] = value === 'Autres';
      this._updateOtherValueValidators(group);
      this._cdRef.markForCheck();
    };

    updateVisibility(nameControl.value);

    nameControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef), tap(updateVisibility))
      .subscribe();
  }

  getFieldControl(fieldName: string): AbstractControl | null {
    return this.billingEquipmentDeclarationform.get(fieldName);
  }

  get equipements(): FormArray<any> {
    return this.billingEquipmentDeclarationform.get('equipments') as FormArray;
  }

  private _addEquipementGroup(): void {
    const group = this.createEquipmentGroup();
    this.equipements.push(group);
    this.showOtherDesignation.push(false);
    this._applyEquipmentValidatorsToGroup(group);
    this._handleDisplayOtherDesignation(group);
    this._handleSelectedType(group);
  }

  private _applyEquipmentValidatorsToGroup(
    group: FormGroup,
    usesBilling = this.equipmentDeclaration().usesBillingEquipment
  ): void {
    const typeControl = group.get('type');
    const nameControl = group.get('name');

    if (usesBilling) {
      typeControl?.setValidators([Validators.required]);
      this._updateNameValidators(group, typeControl?.value);
    } else {
      typeControl?.clearValidators();
      nameControl?.clearValidators();
    }

    typeControl?.updateValueAndValidity();
    nameControl?.updateValueAndValidity();
    this._updateOtherValueValidators(group);
  }

  private _updateOtherValueValidators(group: FormGroup): void {
    const otherControl = group.get('otherValue');
    if (!otherControl) return;

    if (this._shouldRequireOtherValue(group)) {
      otherControl.setValidators([Validators.required]);
    } else {
      otherControl.clearValidators();
      otherControl.setValue(null);
    }

    otherControl.updateValueAndValidity();
  }

  private _shouldRequireOtherValue(group: FormGroup): boolean {
    const typeValue = group.get('type')?.value;
    const index = this.equipements.controls.indexOf(group);
    const showOther = index !== -1 ? this.showOtherDesignation[index] : false;

    return showOther || typeValue === 'Autres';
  }
}
